/**
 * Request validation schemas shared by the API (authoritative validation) and clients
 * (early feedback). The server always re-validates.
 */
import { z } from 'zod';
import { mailPreset } from './mail-presets.js';
import { isAllowedMailPort, mailHostProblem, parseDavUrl, type MailProtocol } from './mail-servers.js';

const uuid = z.uuid();
const email = z.email().max(320);

/** Minimum length for DeskWorks One account passwords (sign-in passwords are not length-checked). */
export const PASSWORD_MIN_LENGTH = 8;
const newPassword = z.string().min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`).max(256);
const shortText = (max: number) => z.string().trim().min(1).max(max);

export const AddressSchema = z.object({
  name: z.string().max(200).nullable().default(null),
  email,
});

/* ---------------------------------------------------------------- Identity */

export const SetupOwnerSchema = z.object({
  /** One-time code from the server operator; required on public servers. */
  setupCode: z.string().max(200).optional(),
  email,
  displayName: shortText(120),
  password: newPassword,
  workspaceName: shortText(80),
  timeZone: z.string().max(64).default('UTC'),
});

export const SignInSchema = z.object({
  email,
  password: z.string().min(1).max(256),
  /** native clients request a bearer session; browsers always receive a cookie session. */
  client: z.enum(['web', 'native']).default('web'),
  deviceLabel: z.string().max(120).optional(),
});

export const AcceptInviteSchema = z.object({
  token: z.string().min(20).max(200),
  displayName: shortText(120),
  password: newPassword,
  timeZone: z.string().max(64).default('UTC'),
});

export const UpdateSettingsSchema = z.object({
  theme: z.enum(['light', 'dark', 'system']).optional(),
  notificationPreviews: z.boolean().optional(),
  quietHours: z
    .object({ enabled: z.boolean(), start: z.string().regex(/^\d{2}:\d{2}$/), end: z.string().regex(/^\d{2}:\d{2}$/) })
    .optional(),
  webCachePolicy: z.enum(['session', 'trusted']).optional(),
  remoteImages: z.enum(['block', 'ask', 'allow']).optional(),
  signatures: z.record(z.uuid(), z.string().max(2000)).optional(),
  mailNotifications: z.record(z.uuid(), z.boolean()).optional(),
  displayName: shortText(120).optional(),
  timeZone: z.string().max(64).optional(),
});

export const CreateInvitationSchema = z.object({
  email,
  role: z.enum(['admin', 'member']).default('member'),
});

/* -------------------------------------------------------------------- Chat */

export const CreateChannelSchema = z.object({
  workspaceId: uuid,
  name: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[\p{L}\p{N} _.-]+$/u, 'Use letters, numbers, spaces, dots, dashes or underscores'),
  topic: z.string().trim().max(250).optional(),
  visibility: z.enum(['public', 'private']).default('public'),
  memberIds: z.array(uuid).max(500).default([]),
});

export const CreateDirectSchema = z.object({
  workspaceId: uuid,
  userIds: z.array(uuid).min(1).max(20),
});

export const PostMessageSchema = z.object({
  /** Client retry key. Re-sending the same key returns the original message. */
  clientKey: z.string().min(8).max(80),
  body: z.string().max(20000),
  threadRootId: uuid.nullable().optional(),
  fileIds: z.array(uuid).max(10).default([]),
});

export const EditMessageSchema = z.object({
  body: z.string().min(1).max(20000),
  /** Optimistic concurrency: the version being edited. */
  version: z.number().int().positive(),
});

export const ReactionSchema = z.object({
  emoji: z.string().min(1).max(32),
});

export const MarkReadSchema = z.object({
  seq: z.number().int().nonnegative(),
  threadRootId: uuid.nullable().optional(),
});

export const UpdateMembershipSchema = z.object({
  addUserIds: z.array(uuid).max(500).default([]),
  removeUserIds: z.array(uuid).max(500).default([]),
});

export const ConversationPrefsSchema = z.object({
  notifyLevel: z.enum(['all', 'mentions', 'mute']),
});

export const ShareMailToChatSchema = z.object({
  mailId: uuid,
  conversationId: uuid,
  /** Exactly the text the user previewed and confirmed. */
  excerpt: z.string().max(20000),
  attachmentIds: z.array(z.string().max(200)).max(10).default([]),
  confirmed: z.literal(true),
  clientKey: z.string().min(8).max(80),
});

/* ------------------------------------------------------------ Mail/Calendar */

export const SpacemailConnectSchema = z.object({
  email,
  /** Mailbox password: sent once over HTTPS to the protected endpoint, never returned. */
  password: z.string().min(1).max(1024),
  syncCalendars: z.boolean().default(true),
  enableContacts: z.boolean().default(false),
});

const mailServerName = z
  .string()
  .trim()
  .toLowerCase()
  .max(253)
  .superRefine((h, ctx) => {
    const problem = mailHostProblem(h);
    if (problem) ctx.addIssue({ code: 'custom', message: problem });
  });

const PORT_COPY: Record<MailProtocol, string> = {
  imap: 'Incoming mail (IMAP) uses port 993 with SSL/TLS or port 143 with STARTTLS.',
  smtp: 'Outgoing mail (SMTP) uses port 465 with SSL/TLS or port 587 with STARTTLS.',
};

const mailEndpoint = (protocol: MailProtocol) =>
  z
    .object({
      host: mailServerName,
      port: z.number().int(),
      /** Plaintext is not an option: TLS from the start, or STARTTLS before anything else. */
      security: z.enum(['implicit-tls', 'starttls']),
    })
    .superRefine((e, ctx) => {
      if (!isAllowedMailPort(protocol, e.port, e.security)) ctx.addIssue({ code: 'custom', path: ['port'], message: PORT_COPY[protocol] });
    });

/**
 * Servers a person enters for "Other (IMAP/SMTP)". The mailbox credential is sent only to
 * exactly these hosts.
 */
export const CustomMailServerSchema = z
  .object({
    imap: mailEndpoint('imap'),
    smtp: mailEndpoint('smtp'),
    /** User name for both servers when it is not the email address. */
    username: z.string().trim().max(320).optional(),
    /** Optional calendar (CalDAV) address, https only. */
    caldavUrl: z.string().trim().max(2000).optional(),
  })
  .superRefine((v, ctx) => {
    if (!v.caldavUrl) return;
    const r = parseDavUrl(v.caldavUrl);
    if (!r.ok) ctx.addIssue({ code: 'custom', path: ['caldavUrl'], message: r.problem });
  });

/**
 * Any IMAP/SMTP mailbox: address + password (usually an app password) with either a reviewed
 * preset or servers the person entered, never both.
 */
export const ImapConnectSchema = z
  .object({
    email,
    /** Mailbox or app password: sent once over HTTPS to the protected endpoint, never returned. */
    password: z.string().min(1).max(1024),
    presetId: z.string().max(40).optional(),
    custom: CustomMailServerSchema.optional(),
    /** Preset only: user name when it is not what the preset expects (iCloud on a custom domain). */
    username: z.string().trim().max(320).optional(),
    syncCalendars: z.boolean().default(true),
  })
  .superRefine((v, ctx) => {
    if (!v.presetId === !v.custom) ctx.addIssue({ code: 'custom', path: ['presetId'], message: 'Choose a provider or enter the server settings.' });
    if (v.presetId && !mailPreset(v.presetId)) ctx.addIssue({ code: 'custom', path: ['presetId'], message: 'This provider is not available.' });
    if (v.custom && v.username) ctx.addIssue({ code: 'custom', path: ['username'], message: 'Put the user name with the server settings.' });
  });

export const DraftUpsertSchema = z.object({
  accountId: uuid,
  to: z.array(AddressSchema).max(200).default([]),
  cc: z.array(AddressSchema).max(200).default([]),
  bcc: z.array(AddressSchema).max(200).default([]),
  subject: z.string().max(998).default(''),
  bodyHtml: z.string().max(2_000_000).default(''),
  inReplyToId: uuid.nullable().default(null),
  forwardOfId: uuid.nullable().default(null),
  /** Files uploaded through /v1/files by the draft owner. Omitted = keep the draft's current files. */
  attachmentFileIds: z.array(uuid).max(20).optional(),
  /** When forwarding: attachments of the original message to include. Omitted = keep current. */
  forwardAttachmentIds: z.array(z.string().max(300)).max(50).optional(),
  /** Optimistic concurrency for updates. */
  version: z.number().int().nonnegative().optional(),
});

export const SendMailSchema = z.object({
  /** Stable operation id shared by every tab/device retry of this send. */
  operationId: uuid,
  draftId: uuid,
  /** The sending account the user saw and confirmed. Must match the draft. */
  accountId: uuid,
});

export const MailActionSchema = z.object({
  operationId: uuid,
  messageIds: z.array(uuid).min(1).max(500),
  action: z.enum(['archive', 'trash', 'markRead', 'markUnread', 'star', 'unstar', 'move']),
  targetFolderId: uuid.optional(),
});

const isoDateTime = z.iso.datetime({ offset: true });

export const EventInputSchema = z.object({
  operationId: uuid,
  calendarId: uuid,
  title: shortText(500),
  start: isoDateTime,
  end: isoDateTime,
  allDay: z.boolean().default(false),
  timeZone: z.string().max(64),
  location: z.string().max(500).nullable().default(null),
  description: z.string().max(20000).nullable().default(null),
  attendees: z.array(AddressSchema).max(200).default([]),
  recurrence: z.string().max(500).nullable().default(null),
  /** Attach a DeskWorks meeting link to this same organizer event. */
  addMeeting: z.boolean().default(false),
});

export const EventUpdateSchema = EventInputSchema.partial().extend({
  operationId: uuid,
  /** 'occurrence' edits one instance; 'series' edits the recurring series. */
  scope: z.enum(['occurrence', 'series']).default('occurrence'),
});

export const RsvpSchema = z.object({
  operationId: uuid,
  status: z.enum(['accepted', 'declined', 'tentative']),
});

/* ---------------------------------------------------------------- Meetings */

export const CreateMeetingSchema = z.object({
  title: shortText(200),
  kind: z.enum(['instant', 'scheduled']),
  workspaceId: uuid.nullable().default(null),
  sourceConversationId: uuid.nullable().default(null),
  startsAt: isoDateTime.optional(),
  endsAt: isoDateTime.optional(),
  lobbyEnabled: z.boolean().default(true),
  guestsAllowed: z.boolean().default(true),
  inviteeIds: z.array(uuid).max(200).default([]),
});

export const LobbyDecisionSchema = z.object({
  participantId: uuid,
  decision: z.enum(['admit', 'deny']),
});

export const ParticipantActionSchema = z.object({
  participantId: uuid,
  action: z.enum(['remove', 'make_cohost', 'mute']),
});

export const OccurrenceControlSchema = z.object({
  action: z.enum(['lock', 'unlock', 'end']),
});

export const GuestJoinSchema = z.object({
  displayName: z.string().trim().min(1).max(80),
});

export const SearchQuerySchema = z.object({
  q: z.string().trim().min(1).max(200),
  scope: z.enum(['all', 'mail', 'events', 'chat', 'people', 'files']).default('all'),
});

export type SetupOwnerInput = z.infer<typeof SetupOwnerSchema>;
export type SignInInput = z.infer<typeof SignInSchema>;
export type AcceptInviteInput = z.infer<typeof AcceptInviteSchema>;
export type UpdateSettingsInput = z.infer<typeof UpdateSettingsSchema>;
export type CreateChannelInput = z.infer<typeof CreateChannelSchema>;
export type CreateDirectInput = z.infer<typeof CreateDirectSchema>;
export type PostMessageInput = z.infer<typeof PostMessageSchema>;
export type EditMessageInput = z.infer<typeof EditMessageSchema>;
export type SpacemailConnectInput = z.infer<typeof SpacemailConnectSchema>;
export type CustomMailServerInput = z.infer<typeof CustomMailServerSchema>;
export type ImapConnectInput = z.input<typeof ImapConnectSchema>;
export type DraftUpsertInput = z.infer<typeof DraftUpsertSchema>;
export type SendMailInput = z.infer<typeof SendMailSchema>;
export type MailActionInput = z.infer<typeof MailActionSchema>;
export type EventInput = z.infer<typeof EventInputSchema>;
export type EventUpdateInput = z.infer<typeof EventUpdateSchema>;
export type CreateMeetingInput = z.infer<typeof CreateMeetingSchema>;
export type ShareMailToChatInput = z.infer<typeof ShareMailToChatSchema>;
